import { useEffect, useRef, useState } from "react";
import { format, subMonths } from "date-fns";
import { ReactComponent as CloseIcon } from "../../console/assets/icons/closeIcon.svg";
import { ReactComponent as RadioIcon } from "../../console/assets/icons/radioIcon.svg";
import CheckBox from "../../console/components/common/checkBox";
import DayPickerInput from "../../console/components/common/DayPickerInput";
import Button from "../../common/components/button";
import { usePostOrderExcelDownload } from "../../common/hooks/console/transaction/usePostOrderExcelDownload";

/**
 * 지점 포털 엑셀 다운로드
 *
 * 본점 Excel 컴포넌트는 주문양식/청구서/타지점정산까지 함께 다루는데,
 * 지점은 주문엑셀 다운로드 하나만 쓴다. 나머지 엔드포인트는 1계층에서 차단된다.
 *
 * 발주처 스코프와 금액 컬럼 제거는 서버가 강제하므로 화면에서 보내는 값과 무관하다.
 */
const DEFAULT_ORDER_STATUS = [1, 2, 3, 4, 5, 6];

const ORDER_STATUS_LABELS = ["미확인", "접수완료", "준비완료", "배송완료", "취소", "보류"];

const DATE_OPTIONS = [
    { value: "today", label: "오늘", monthsAgo: 0 },
    { value: "month", label: "1개월", monthsAgo: 1 },
    { value: "threeMonths", label: "3개월", monthsAgo: 3 },
    { value: "sixMonths", label: "6개월", monthsAgo: 6 },
    { value: "year", label: "1년", monthsAgo: 12 },
];

type BranchExcelProps = {
    selectedIds: number[];
    startDate?: string;
    endDate?: string;
    orderStatus?: number[];
    onClose: () => void;
    onError: (message: string) => void;
};

const BranchExcel = ({ selectedIds, startDate, endDate, orderStatus, onClose, onError }: BranchExcelProps) => {
    const [selectedOption, setSelectedOption] = useState("");
    const [start, setStart] = useState<Date | undefined>(startDate ? new Date(startDate) : undefined);
    const [end, setEnd] = useState<Date | undefined>(endDate ? new Date(endDate) : undefined);
    const [status, setStatus] = useState<number[]>(orderStatus?.length ? orderStatus : DEFAULT_ORDER_STATUS);
    const [selectedOnly, setSelectedOnly] = useState(selectedIds.length > 0);

    const excelRef = useRef<HTMLDivElement>(null);

    const { mutate: downloadExcel, isPending } = usePostOrderExcelDownload(
        () => onClose(),
        error => onError(error?.response?.data?.message || "엑셀 다운로드에 실패했습니다."),
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                excelRef.current &&
                !excelRef.current.contains(event.target as Node) &&
                !(event.target as HTMLElement).closest(".rdp")
            ) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const handleDateSelection = (value: string, monthsAgo: number) => {
        const today = new Date();
        setSelectedOption(value);
        setStart(monthsAgo === 0 ? today : subMonths(today, monthsAgo));
        setEnd(today);
    };

    const toggleStatus = (value: number) => {
        setStatus(prev => (prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]));
    };

    const handleDownload = () => {
        downloadExcel({
            order_idx: selectedOnly ? selectedIds : [],
            order_status: status.length ? status : DEFAULT_ORDER_STATUS,
            payment_status: [],
            branch_type: [],
            delivery_name: [],
            customer_idx: [],
            customer_branch_idx: [],
            start_date: selectedOnly || !start ? undefined : format(start, "yyyy-MM-dd"),
            start_date_time: "00:00:00",
            end_date: selectedOnly || !end ? undefined : format(end, "yyyy-MM-dd"),
            end_date_time: "23:59:59",
        });
    };

    return (
        <div ref={excelRef} className="w-[560px] bg-white rounded-2xl border shadow-lg">
            <div className="flex items-center justify-between px-5 py-6">
                <p className="text-2xl font-bold">엑셀 다운로드</p>
                <CloseIcon className="cursor-pointer" onClick={onClose} />
            </div>

            <div className="bg-[#F6F7FA] flex flex-col gap-3 p-5 max-h-[60vh] overflow-y-auto">
                <div className="flex items-center">
                    <p className="w-[100px] text-sm text-gray2">범위</p>
                    <div className="w-full text-base flex gap-4">
                        <CheckBox
                            label={`선택한 ${selectedIds.length}건만`}
                            checked={selectedOnly}
                            disabled={selectedIds.length === 0}
                            onChange={() => setSelectedOnly(prev => !prev)}
                        />
                        <CheckBox label="조회 조건 전체" checked={!selectedOnly} onChange={() => setSelectedOnly(false)} />
                    </div>
                </div>

                <hr className="border-gray4" />

                <div className={`flex items-start ${selectedOnly ? "opacity-40 pointer-events-none" : ""}`}>
                    <p className="w-[100px] text-sm text-gray2">배송일자</p>
                    <div className="w-full flex flex-col gap-2">
                        <div className="grid grid-cols-5 h-[34px]">
                            {DATE_OPTIONS.map(option => (
                                <div
                                    key={option.value}
                                    className={`flex items-center w-full gap-2 cursor-pointer ${
                                        selectedOption === option.value ? "text-primary2" : "text-gray2"
                                    }`}
                                    onClick={() => handleDateSelection(option.value, option.monthsAgo)}
                                >
                                    {selectedOption === option.value ? (
                                        <RadioIcon className="w-8 h-8 -ml-1.5" />
                                    ) : (
                                        <div className="border-gray4 h-[19px] w-[19px] border-2 rounded-full" />
                                    )}
                                    <span className={`${selectedOption === option.value && "-ml-[7px]"}`}>{option.label}</span>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-3 relative z-50 bg-white h-[34px] px-3 border border-gray4 rounded-lg">
                            <DayPickerInput
                                twoCalendar
                                maxDate={end}
                                initialDate={start}
                                onDateChange={date => {
                                    if (!date) return;
                                    setStart(date);
                                    setSelectedOption("");
                                }}
                            />
                            <DayPickerInput
                                twoCalendar
                                minDate={start}
                                initialDate={end}
                                onDateChange={date => {
                                    if (!date) return;
                                    setEnd(date);
                                    setSelectedOption("");
                                }}
                            />
                        </div>
                    </div>
                </div>

                <hr className="border-gray4" />

                <div className={`flex items-start ${selectedOnly ? "opacity-40 pointer-events-none" : ""}`}>
                    <p className="w-[100px] text-sm text-gray2">주문상태</p>
                    <div className="w-full text-base grid grid-cols-4 gap-2">
                        {ORDER_STATUS_LABELS.map((label, index) => (
                            <CheckBox
                                key={label}
                                label={label}
                                checked={status.includes(index + 1)}
                                onChange={() => toggleStatus(index + 1)}
                            />
                        ))}
                    </div>
                </div>

                <p className="text-xs text-gray3">판매금액·배송금액 컬럼은 포함되지 않습니다.</p>
            </div>

            <div className="flex items-center justify-between px-6 h-[84px] gap-2">
                <Button size="L" className="w-full" onClick={onClose}>
                    취소
                </Button>
                <Button size="L" className="w-[360px] flex-shrink-0" status="regist" disabled={isPending} onClick={handleDownload}>
                    {isPending ? "다운로드 중..." : "다운로드"}
                </Button>
            </div>
        </div>
    );
};

export default BranchExcel;
